import React, { useState } from "react";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { FiX, FiUploadCloud } from "react-icons/fi";

export default function EditPracticeModal({ data, onClose, onSuccess }) {
  const [name, setName] = useState(data.name);
  const [paper, setPaper] = useState(data.paper);
  const [icon, setIcon] = useState(data.icon);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleIconChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const res = await uploadFiles({ files: [file] });
      setIcon(res?.data?.imageUrl);
      showSuccessToast("Icon updated successfully");
    } catch (err) {
      showErrorToast(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await makeRequestAuth("post", "/admin/updatePractice", {
        practiceId: data._id,
        name,
        icon,
        paper: Number(paper),
      });
      showSuccessToast("Practice updated");
      onSuccess();
    } catch {
      showErrorToast("Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm transition-all">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        {/* HEADER */}
        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50">
          <h3 className="text-lg font-bold text-gray-800">Edit Practice</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 text-gray-500 transition"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* UPLOAD AREA */}
          <div className="flex items-center gap-4 border p-3 rounded-xl bg-gray-50/50">
            <div className="h-16 w-16 bg-white border rounded-lg flex items-center justify-center p-1 shrink-0">
              {uploading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              ) : (
                <img
                  src={icon}
                  alt="preview"
                  className="h-full w-full object-contain"
                />
              )}
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Update Icon
              </label>
              <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
                <FiUploadCloud />
                <span>Click to replace</span>
                <input
                  type="file"
                  className="hidden"
                  onChange={handleIconChange}
                  accept="image/*"
                />
              </label>
            </div>
          </div>

          {/* FORM FIELDS */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Practice Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Total Papers
              </label>
              <input
                type="number"
                value={paper}
                onChange={(e) => setPaper(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              />
            </div>
          </div>

          {/* FOOTER */}
          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-lg shadow-orange-500/30 font-medium disabled:opacity-50 transition-all"
            >
              {loading ? "Updating..." : "Update Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
